import React, { useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  useTheme,
} from '@mui/material';
import { CheckCircle, Euro, OpenInNew, Assessment } from '@mui/icons-material';
import { motion, useInView } from 'framer-motion';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { getGlassCardStyles } from '../../shared/styles';

const contentUseCase = new ContentUseCase();

export const WhatIsMEPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const meInfo = contentUseCase.getWhatIsMEContent(language);
  
  // Refs for sections
  const titleRef = useRef(null);
  const keyFactsRef = useRef(null);
  const symptomsRef = useRef(null);
  const longCovidRef = useRef(null);
  const disablingRef = useRef(null);
  const prevalenceRef = useRef(null);
  const livingRef = useRef(null);
  const economicRef = useRef(null);
  const impactRef = useRef(null);
  
  const titleInView = useInView(titleRef, { once: true });
  const keyFactsInView = useInView(keyFactsRef, { once: true, margin: "-100px" });
  const symptomsInView = useInView(symptomsRef, { once: true, margin: "-100px" });
  const longCovidInView = useInView(longCovidRef, { once: true, margin: "-100px" });
  const disablingInView = useInView(disablingRef, { once: true, margin: "-100px" });
  const prevalenceInView = useInView(prevalenceRef, { once: true, margin: "-100px" });
  const livingInView = useInView(livingRef, { once: true, margin: "-100px" });
  const economicInView = useInView(economicRef, { once: true, margin: "-100px" });
  const impactInView = useInView(impactRef, { once: true, margin: "-100px" });
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const getColorForScore = (score: number) => {
    if (score <= 0.5) return theme.palette.error.main;
    if (score <= 0.7) return theme.palette.warning.main;
    if (score <= 0.8) return theme.palette.primary.main;
    return theme.palette.success.main;
  };

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(8) }}>
      <SEO page="whatIsME" />
      <motion.div
        ref={titleRef}
        initial={{ opacity: 0, y: 20 }}
        animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: theme.spacing(6) }}>
          <Box
            component="img"
            src="/icon.png"
            alt=""
            aria-hidden="true"
            sx={{ height: { xs: 44, md: 56 }, width: 'auto' }}
          />
          <Typography variant="h2" component="h1" color="primary" sx={{ fontWeight: 800 }}>
            {meInfo.title}
          </Typography>
        </Box>
      </motion.div>
      {/* Neuromunological Disease Info */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] as const }}
      >
        <Box sx={{ mb: theme.spacing(8) }}>
          {meInfo.description.split('\n').map((paragraph: string, index: number) => (
            <Typography
              key={`paragraph-${paragraph.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
              variant={index === 0 ? 'h6' : 'body1'}
              color="text.secondary"
              paragraph
              align="center"
              sx={{
                maxWidth: { xs: '100%', md: 1000 },
                mx: 'auto',
                mb:
                  index === 0
                    ? theme.spacing(4)
                    : index < meInfo.description.split('\n').length - 1
                      ? theme.spacing(3)
                      : 0,
              }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      </motion.div>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Key Facts Section */}
      <Box sx={{ mb: theme.spacing(8) }} ref={keyFactsRef}>
        <motion.div
          initial="hidden"
          animate={keyFactsInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.keyFacts.title}
          </Typography>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={keyFactsInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: theme.spacing(4),
              justifyContent: 'center',
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
            }}
          >
            {meInfo.keyFacts.cards.map((card: any) => {
              // Map each key fact to its corresponding section
              const getTargetRef = () => {
                if (card.icon === 'People') return prevalenceRef; // Global Impact → Prevalence
                if (card.icon === 'Virus') return longCovidRef; // Viral Triggers → Symptoms
                if (card.icon === 'Health') return disablingRef; // Severe Disability → Most Disabling
                return null;
              };

              const targetRef = getTargetRef();
              
              const handleClick = () => {
                if (targetRef && targetRef.current) {
                  const element = targetRef.current as HTMLElement;
                  const headerHeight = window.innerWidth >= 900 ? 70 : 64; // Match Layout header height
                  const offset = headerHeight + 20; // Add extra padding
                  
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;

                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              };

              return (
                <motion.div
                  key={`keyfact-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
                  variants={fadeInUp}
                  style={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 300 }}
                >
                  <Card 
                    onClick={handleClick}
                    sx={{ 
                      textAlign: 'center', 
                      height: '100%', 
                      cursor: 'pointer',
                      ...getGlassCardStyles(theme, 'default', false)
                    }}
                  >
                    <CardContent sx={{ p: theme.spacing(4) }}>
                      <Box
                        sx={{
                          width: theme.spacing(8),
                          height: theme.spacing(8),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: theme.spacing(2),
                          color: 'primary.main',
                          fontSize: theme.spacing(4),
                          fontWeight: 'bold',
                        }}
                      >
                        {card.icon === 'People' && '👥'}
                        {card.icon === 'Virus' && '🦠'}
                        {card.icon === 'Health' && '🏥'}
                      </Box>
                      <Typography variant="h5" component="h3" gutterBottom color="secondary" sx={{ fontWeight: 600 }}>
                        {card.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </Box>
        </motion.div>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Why This Matters */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.whyThisMatters.title}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.whyThisMatters.description}
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Symptoms Section */}
      <Box sx={{ mb: theme.spacing(8) }} ref={symptomsRef}>
        <motion.div
          initial="hidden"
          animate={symptomsInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.symptoms.title}
          </Typography>

        {/* PEM - Cardinal Symptom */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography
            variant="h5"
            sx={{ mb: theme.spacing(3), fontWeight: 600, textAlign: 'center', color: 'primary.main' }}
          >
            {meInfo.symptoms.pemTitle}
          </Typography>
          <Typography
            variant="body1"
            color="text.primary"
            sx={{ textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}
          >
            {meInfo.pemDescription}
          </Typography>
        </Box>

        {/* Other Symptoms */}
          <Typography variant="h4" component="h3" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.symptoms.otherSymptomsTitle}
          </Typography>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={symptomsInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', ...getGlassCardStyles(theme, 'default', true) }}>
            <CardContent sx={{ p: theme.spacing(4) }}>
              <List>
                {meInfo.symptoms.list.map((symptom: string) => (
                  <ListItem key={`symptom-${symptom.toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: theme.spacing(2) }}>
                    <ListItemIcon>
                      <CheckCircle color="secondary" sx={{ fontSize: theme.spacing(3.5) }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={symptom}
                      primaryTypographyProps={{
                        variant: 'h6',
                        sx: { fontWeight: 500 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </motion.div>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Long COVID Section */}
      <Box sx={{ mb: theme.spacing(8) }} ref={longCovidRef}>
        <motion.div
          initial="hidden"
          animate={longCovidInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.longCovid.title}
          </Typography>
          <Typography
            variant="h6"
            paragraph
            align="center"
            sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}
          >
            {meInfo.longCovid.description}
          </Typography>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={longCovidInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Card
            sx={{
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
              mb: theme.spacing(4),
              backgroundColor: `${theme.palette.primary.main} !important`,
              backdropFilter: 'blur(25px) saturate(180%)',
              WebkitBackdropFilter: 'blur(25px) saturate(180%)',
              border: `2px solid ${theme.palette.secondary.main}60`,
              borderRadius: '24px',
              boxShadow: `
                0 12px 40px rgba(0, 0, 0, 0.15),
                0 4px 16px ${theme.palette.primary.main}30,
                inset 0 1px 0 rgba(255, 255, 255, 0.2),
                inset 0 -1px 0 rgba(0, 0, 0, 0.1)
              `,
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: `${theme.palette.primary.dark} !important`,
                border: `2px solid ${theme.palette.primary.main}80`,
                boxShadow: `
                  0 16px 48px rgba(0, 0, 0, 0.18),
                  0 8px 24px ${theme.palette.primary.main}35,
                  inset 0 1px 0 rgba(255, 255, 255, 0.25),
                  inset 0 -1px 0 rgba(0, 0, 0, 0.15)
                `,
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent sx={{ p: theme.spacing(3) }}>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: 'white',
                  fontStyle: 'italic',
                  fontWeight: 500,
                  mb: 0,
                }}
              >
                {meInfo.longCovid.statement}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={longCovidInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', ...getGlassCardStyles(theme, 'default', true) }}>
            <CardContent sx={{ p: theme.spacing(4) }}>
              <List>
                {meInfo.longCovid.keyPoints.map((point: string) => (
                  <ListItem key={`longcovid-point-${point.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: theme.spacing(1) }}>
                    <ListItemIcon>
                      <CheckCircle color="secondary" sx={{ fontSize: theme.spacing(3) }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={point}
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { fontWeight: 500 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </motion.div>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Most Disabling Illness Section */}
      <Box sx={{ mb: theme.spacing(8) }} ref={disablingRef}>
        <motion.div
          initial="hidden"
          animate={disablingInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.mostDisabling.title}
          </Typography>
          <Typography
            variant="h5"
            paragraph
            align="center"
            sx={{
              mb: theme.spacing(6),
              color: 'text.secondary',
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
              fontSize: '1.2rem',
            }}
          >
            {meInfo.mostDisabling.description}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={disablingInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', ...getGlassCardStyles(theme, 'default', true) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: theme.spacing(4) }}>
                <Assessment sx={{ mr: theme.spacing(2), color: 'error.main', fontSize: theme.spacing(4) }} />
                <Typography variant="h4" component="h3" align="center">
                  {meInfo.mostDisabling.study.title}
                </Typography>
              </Box>

              <Typography variant="body1" paragraph sx={{ mb: theme.spacing(4), color: 'text.secondary', textAlign: 'center' }}>
                {meInfo.mostDisabling.study.explanation}
              </Typography>

              <Box sx={{ mb: theme.spacing(3), maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
                {meInfo.mostDisabling.study.data.map((item: any) => (
                  <Box key={`quality-${item.condition.toLowerCase().replace(/\s+/g, '-')}`} sx={{ mb: theme.spacing(3) }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {item.condition}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        {item.score.toFixed(2)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={item.score * 100}
                      sx={{
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getColorForScore(item.score),
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              <Button
                variant="outlined"
                color="secondary"
                href={meInfo.mostDisabling.study.source}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNew />}
                sx={{ mt: theme.spacing(2) }}
              >
                {meInfo.mostDisabling.study.buttonText}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Prevalence Section */}
      <Box sx={{ mb: theme.spacing(8) }} ref={prevalenceRef}>
        <motion.div
          initial="hidden"
          animate={prevalenceInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.prevalence.title}
          </Typography>
          <Typography
            variant="h6"
            paragraph
            align="center"
            sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}
          >
            {meInfo.prevalence.description}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={prevalenceInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', ...getGlassCardStyles(theme, 'default', true) }}>
            <CardContent>
              <Typography variant="h5" component="h3" gutterBottom align="center" sx={{ mb: theme.spacing(3) }}>
                {meInfo.prevalence.globalStats.title}
              </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: { xs: '100%', md: 800 }, mx: 'auto', mb: theme.spacing(3) }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{meInfo.prevalence.globalStats.tableHeaders.region}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{meInfo.prevalence.globalStats.tableHeaders.cases}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{meInfo.prevalence.globalStats.tableHeaders.populationPercent}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meInfo.prevalence.globalStats.data.map((item: any) => (
                    <TableRow key={`global-${item.region.toLowerCase().replace(/\s+/g, '-')}`}>
                      <TableCell>{item.region}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.cases}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.percentage}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: theme.spacing(1), alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(1) }}>
                {meInfo.prevalence.sourcesLabel}
              </Typography>
              {meInfo.prevalence.sources.map((source: any) => (
                <Button
                  key={`source-${source.name.toLowerCase().replace(/\s+/g, '-')}`}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNew />}
                  sx={{ minWidth: 120 }}
                >
                  {source.name}
                </Button>
              ))}
            </Box>
          </CardContent>
        </Card>
        </motion.div>

        {/* Underdiagnosis Section */}
        <Box sx={{ mt: theme.spacing(6), textAlign: 'center' }}>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mb: theme.spacing(3), color: 'primary.main' }}>
            {meInfo.prevalence.underdiagnosis.title}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: { xs: '100%', md: 800 }, mx: 'auto', color: 'text.secondary' }}>
            {meInfo.prevalence.underdiagnosis.description}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* What Does Living with ME Mean */}
      <Box sx={{ mb: theme.spacing(8) }} ref={livingRef}>
        <motion.div
          initial="hidden"
          animate={livingInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.livingWithME.title}
          </Typography>
          <Typography
            variant="h6"
            paragraph
            align="center"
            sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}
          >
            {meInfo.livingWithME.description}
          </Typography>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={livingInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: theme.spacing(3),
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
            }}
          >
            {meInfo.livingWithME.stages.map((stage: any) => (
              <motion.div
                key={`stage-${stage.severity.toLowerCase().replace(/\s+/g, '-')}`}
                variants={fadeInUp}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }
                }}
                style={{ flex: '1 1 calc(50% - 12px)', minWidth: 300 }}
              >
                <Card sx={{ height: '100%', ...getGlassCardStyles(theme, 'default', true) }}>
                  <CardContent sx={{ p: theme.spacing(4), textAlign: 'center' }}>
                    <Typography variant="h4" component="h3" gutterBottom color="secondary" sx={{ fontWeight: 700 }}>
                      {stage.severity} ME
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {stage.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Economic Impact Section */}
      <Box sx={{ mb: theme.spacing(8) }} ref={economicRef}>
        <motion.div
          initial="hidden"
          animate={economicInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {meInfo.economicImpact.title}
          </Typography>
          <Typography
            variant="h6"
            paragraph
            align="center"
            sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}
          >
            {meInfo.economicImpact.description}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={economicInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', ...getGlassCardStyles(theme, 'default', true) }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: theme.spacing(4) }}>
              <Euro sx={{ mr: theme.spacing(2), color: 'warning.main', fontSize: theme.spacing(4) }} />
              <Typography variant="h4" component="h3" align="center">
                {meInfo.economicImpact.study.title}
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{meInfo.economicImpact.study.tableHeaders.year}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{meInfo.economicImpact.study.tableHeaders.cost}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{meInfo.economicImpact.study.tableHeaders.gdpImpactPercent}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meInfo.economicImpact.study.data.map((item: any) => (
                    <TableRow key={`economic-${item.year.toLowerCase().replace(/\s+/g, '-')}`}>
                      <TableCell>{item.year}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.cost}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.gdpImpact}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="outlined"
              color="secondary"
              href={meInfo.economicImpact.study.source}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNew />}
              sx={{ mt: theme.spacing(3) }}
            >
              {meInfo.economicImpact.study.buttonText}
            </Button>
          </CardContent>
        </Card>
        </motion.div>
      </Box>

      <Box
        sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'secondary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }}
      />

      {/* Impact on Patients */}
      <Box sx={{ mb: theme.spacing(8) }} ref={impactRef}>
        <motion.div
          initial="hidden"
          animate={impactInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.impactOnPatients.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            maxWidth: { xs: '100%', md: 1000 },
            mx: 'auto',
            mb: theme.spacing(4),
            color: 'text.primary',
          }}
        >
          {meInfo.impactOnPatients.description}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            maxWidth: { xs: '100%', md: 1000 },
            mx: 'auto',
            mb: theme.spacing(4),
            color: 'text.primary',
          }}
        >
          {meInfo.impactOnPatients.additionalInfo}
        </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {meInfo.impactOnPatients.familyImpact}
          </Typography>
        </motion.div>
      </Box>

    </Container>
  );
};

